import { useCallback, useEffect, useState } from 'react';
import {
  type Application,
  type ApplicationStatus,
  type DraftOutreachResult,
  draftOutreach,
  listApplications,
  transitionApplication,
} from './api';
import {
  INITIAL_STAGE_MOVE_STATE,
  applyOptimisticStageMove,
  confirmStageMove,
  rollbackStageMove,
} from './stageMove';

/**
 * The Track tab's data hook — the Throughline analogue of apps/web/src/pages
 * /Tracker.tsx's own inline load/move logic, but split into its own hook
 * (matching this app's house pattern: useJobsInbox for Jobs, useCorpusCanvas
 * for Refine) so the page component stays presentation-only. Departs from
 * apps/web's Tracker (which moves optimistically but has no rollback state
 * machine of its own — it just re-fetches on failure, Tracker.tsx:41-48) by
 * using the SAME optimistic + server-confirm + error-rollback discipline
 * useCorpusCanvas's rehome already establishes for this app — one house
 * pattern for every drag/move interaction in Throughline, not two.
 */

export interface ApplicationsBoardState {
  applications: Application[];
  loading: boolean;
  error: string | null;
}

export function useApplicationsBoard() {
  const [state, setState] = useState<ApplicationsBoardState>({
    applications: [],
    loading: true,
    error: null,
  });
  const [moveState, setMoveState] = useState(INITIAL_STAGE_MOVE_STATE);
  const [moveError, setMoveError] = useState<string | null>(null);
  // GAP 4 — the notes-save affordance's own in-flight/error tracking, kept
  // separate from moveState/moveError: a notes save is a PATCH to the SAME
  // status the card already has (a no-op transition per planTransition — see
  // saveNotes below), never a stage MOVE, so it should never surface as a
  // board-wide "move" error or block a genuine drag/select move on another
  // card. `notesSavingId` is per-card (like moveState.pendingId) so only the
  // ONE card being saved shows its own "Saving…" state.
  const [notesSavingId, setNotesSavingId] = useState<string | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const applications = await listApplications();
      setState({ applications, loading: false, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Move an application to a new stage: optimistic update -> PATCH -> adopt
   *  the server's own returned row -> rollback on failure. No-op guard: a
   *  move to the SAME status the card already has is skipped entirely (no
   *  round trip, no visible flicker) — the backend itself treats this as a
   *  valid no-op (applicationTransition.test.ts), but there is nothing to
   *  visually confirm, so this hook does not bother the server with it. */
  const moveStage = useCallback(
    async (id: string, toStatus: ApplicationStatus) => {
      const current = state.applications.find((a) => a.id === id);
      if (!current || current.status === toStatus) return;

      setMoveError(null);
      const { next, state: nextMoveState } = applyOptimisticStageMove(state.applications, id, toStatus);
      setState((s) => ({ ...s, applications: next }));
      setMoveState(nextMoveState);

      try {
        const { application } = await transitionApplication(id, toStatus);
        const { next: confirmed, state: confirmedMoveState } = confirmStageMove(next, application);
        setState((s) => ({ ...s, applications: confirmed }));
        setMoveState(confirmedMoveState);
      } catch (err) {
        const { restored } = rollbackStageMove(nextMoveState);
        setMoveState(INITIAL_STAGE_MOVE_STATE);
        if (restored) setState((s) => ({ ...s, applications: restored }));
        setMoveError(err instanceof Error ? err.message : 'Could not move that application — try again.');
      }
    },
    [state.applications],
  );

  /** Draft a follow-up email for an application's job (POST /outreach/draft,
   *  kind:'follow_up'). Returns the FULL discriminated DraftOutreachResult —
   *  success, a grounded refusal (422, reason+message), or a Gmail-connection
   *  failure (409/403) — so the caller (Tracker.tsx) can render whichever
   *  branch actually came back, verbatim, per the brief. This hook does not
   *  collapse any of those branches into a single error string. */
  const draftFollowUp = useCallback(async (jobId: string): Promise<DraftOutreachResult> => {
    return draftOutreach({ jobId, kind: 'follow_up' });
  }, []);

  /**
   * GAP 4 — save (or clear) an application's own note. Calls PATCH
   * /applications/:id (transitionApplication) with the SAME status the card
   * already has — TransitionInput requires `status` on every call (it is not
   * optional server-side), so a notes-only edit resends the current status
   * rather than a real transition; planTransition treats a same-status PATCH
   * as a no-op (no applicationEvents row, no appliedAt/closedAt side effect —
   * applicationTransition.ts), so this can never masquerade as a stage move.
   * Adopts the server's own returned row on success (never trusts the locally
   * typed note back) — same "the served truth always wins" discipline
   * moveStage already follows above. A save failure is reported per-card via
   * notesError; the caller (StageCard) decides how to keep the user's
   * unsaved draft text visible so a failed save never silently discards what
   * they typed.
   */
  const saveNotes = useCallback(
    async (id: string, notes: string) => {
      const current = state.applications.find((a) => a.id === id);
      if (!current) return;

      setNotesError(null);
      setNotesSavingId(id);
      try {
        const { application } = await transitionApplication(id, current.status, { notes });
        setState((s) => ({
          ...s,
          applications: s.applications.map((a) => (a.id === id ? application : a)),
        }));
      } catch (err) {
        setNotesError(
          err instanceof Error ? err.message : 'Could not save that note — try again.',
        );
        throw err;
      } finally {
        setNotesSavingId(null);
      }
    },
    [state.applications],
  );

  return {
    state,
    refresh,
    moveState,
    moveError,
    moveStage,
    draftFollowUp,
    notesSavingId,
    notesError,
    saveNotes,
  };
}
